import { useEffect, useState } from 'react';

import UnfoldMoreIcon from '@assets/buttons-icons/expand-up-down.svg?react';
import palette from '@lib/core/src/theme/palette';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import { Box, Popover, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { EmailReplyScope, usePatchUserAgentsConfig } from '@/api/generated';
import { useUserAgents } from '@/hooks/useUserAgents';

import {
  EmailConfigTitle,
  EmailCriteriaContainer,
  EmailScopeButton,
  SelectTimeframeButton,
  TimeframeOption
} from './AgentReplayStyles';

const timeframeConfig = [
  {
    name: '1 day',
    value: 1
  },
  {
    name: '2 days',
    value: 2
  },
  {
    name: '3 days',
    value: 3
  },
  {
    name: '1 week',
    value: 7
  }
];

export const AgentReplay = () => {
  const queryClient = useQueryClient();
  const { data: agents } = useUserAgents();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isActiveScope, setIsActiveScope] = useState<EmailReplyScope | undefined>();
  const [timeframe, setTimeframe] = useState<string | undefined>();

  const { mutate } = usePatchUserAgentsConfig({
    mutation: {
      onSuccess: async () => await queryClient.invalidateQueries({ queryKey: ['userAgentsConfig'] })
    }
  });

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onEmailScopeClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const buttonScopeId = e.currentTarget.id as EmailReplyScope;
    mutate({
      data: {
        emailReply: {
          scope: buttonScopeId
        }
      }
    });
    setIsActiveScope(buttonScopeId);
  };

  const onTimeframeClick = (name: string, value: number) => {
    mutate({
      data: {
        emailReply: {
          unRepliedDays: value
        }
      }
    });
    setTimeframe(name);
    setAnchorEl(null);
  };

  useEffect(() => {
    if (!agents?.items?.length) return;
    const agentDetails = agents.items[0];
    setIsActiveScope(agentDetails?.emailReply?.scope ?? EmailReplyScope.all);
    const timeframeLabel = timeframeConfig.find(
      ({ value }) => value === agentDetails?.emailReply?.unRepliedDays
    )?.name;
    setTimeframe(timeframeLabel || timeframeConfig[0].name);
  }, [agents]);

  const open = Boolean(anchorEl);

  return (
    <Stack gap={'16px'}>
      <Typography sx={{ color: palette.black, fontSize: '13px' }}>
        Emails the agent will scan
      </Typography>
      <Box>
        <EmailConfigTitle>Email Scope</EmailConfigTitle>
        <Stack gap={'6px'} flexDirection={'row'}>
          <EmailScopeButton
            isActive={isActiveScope === EmailReplyScope.all}
            id={EmailReplyScope.all}
            onClick={onEmailScopeClick}
            startIcon={<AllInclusiveIcon />}
          >
            All Emails
          </EmailScopeButton>
          <EmailScopeButton
            isActive={isActiveScope === EmailReplyScope.internal}
            id={EmailReplyScope.internal}
            onClick={onEmailScopeClick}
            startIcon={<GroupOutlinedIcon />}
          >
            Internal Only
          </EmailScopeButton>
          <EmailScopeButton
            isActive={isActiveScope === EmailReplyScope.external}
            id={EmailReplyScope.external}
            onClick={onEmailScopeClick}
            startIcon={<LanguageIcon />}
          >
            External Only
          </EmailScopeButton>
        </Stack>
      </Box>
      <Box>
        <EmailConfigTitle>Email Criteria</EmailConfigTitle>
        <EmailCriteriaContainer>
          <DraftsOutlinedIcon />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '12px', color: palette.black }}>
              Unanswered emails
            </Typography>
            <Typography sx={{ fontSize: '11px', color: palette.grey[500] }}>
              That didn’t get attention for specified time
            </Typography>
          </Box>
          <SelectTimeframeButton onClick={handleClick}>
            {timeframe}
            <UnfoldMoreIcon style={{ marginLeft: '5px' }} fontSize="small" />
          </SelectTimeframeButton>
        </EmailCriteriaContainer>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          slotProps={{
            paper: {
              sx: {
                maxHeight: '300px'
              }
            }
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
        >
          {timeframeConfig.map(({ name, value }) => (
            <TimeframeOption key={name} onClick={() => onTimeframeClick(name, value)}>
              <Typography sx={{ fontSize: '13px', color: palette.black }}>{name}</Typography>
            </TimeframeOption>
          ))}
        </Popover>
      </Box>
    </Stack>
  );
};
